import { useState, useEffect, useRef } from "react";
import { Request } from "../../../services/Request";
import { urls } from "../../../Utils/Urls";
import { useTranslation } from "react-i18next";

export const useGlobalSearch = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { i18n } = useTranslation();

  const debounceTimeout = useRef(null);

  useEffect(() => {
    if (query.length < 3) {
      setResults([]);
      setLoading(false);
      return;
    }

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    setLoading(true);

    debounceTimeout.current = setTimeout(async () => {
      try {
        // Proxied through the shared agent/staticData gateway
        // (?service=global_search) instead of hitting
        // /dfs-personalization/search/v3 directly. The gateway expects the
        // requestBody/requestParams envelope and returns the same
        // { results: [...] } shape; Request() injects RequestInfo.authToken.
        // See .agent/weather/static-data-gateway.md.
        //
        // This live/debounced search is browse-only and deliberately carries NO
        // farmer userInfo, so it does NOT record to recent-search history — that
        // would pollute history with every partial keystroke (e.g. "ban",
        // "banan", "banana"). Recording is instead a commit-time action, see
        // recordCommittedSearch() wired into GlobalSearchInput.
        const response = await Request({
          url: urls.global_search,
          params: { service: "global_search" },
          data: {
            requestBody: {
              GlobalSearchCriteria: {
                type: "text",
                data: query.trim(),
                locale: i18n.language?.startsWith("hi") ? "hi" : "en",
              },
              RequestInfo: {
                apiId: "dfs-web",
                ver: "1.0",
                action: "search",
                msgId: "search-request",
              },
            },
            requestParams: { tenantId: "br" },
          },
        });

        if (response?.data?.results) {
          // Advisory hits return `/agri-tips-details-screen`, which has no web
          // equivalent yet — drop them so the dropdown only shows actionable
          // rows. See .agent/globalSearch/unmapped_web_routes.md.
          const actionable = response.data.results.filter(
            (r) => r?.route !== "/agri-tips-details-screen"
          );

          // Group by category
          const groupedResults = actionable.reduce((acc, result) => {
            const cat = result.category || "Other";
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(result);
            return acc;
          }, {});

          const groupedArray = Object.keys(groupedResults).map((category) => ({
            category,
            items: groupedResults[category],
          }));

          setResults(groupedArray);
        } else {
          setResults([]);
        }
      } catch (err) {
        console.error("Global search error:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [query, i18n.language]);

  return { query, setQuery, results, setResults, loading, error };
};
